/**
 * StateEditor implementation manages the JSON editor UI state through signals.
 * It provides methods to start editing (loading current state into editable JSON),
 * cancel editing, and reset to a specific game state. The editable JSON is stored
 * as a string that can be modified by the UI component, and errors are cleared
 * automatically when JSON changes or editing is cancelled.
 */
import { signal } from '@preact/signals-react'
import type { StateEditor, StateEditorDeps } from './stateEditor.types'

export function makeStateEditor(_deps: StateEditorDeps = {}): StateEditor {
  const isEditing = signal<boolean>(false)
  const editableJson = signal<string>('')
  const isApplying = signal<boolean>(false)
  const error = signal<string | null>(null)

  function setIsEditing(value: boolean): void {
    isEditing.value = value
  }

  function setEditableJson(json: string): void {
    editableJson.value = json
    // Clear error when JSON changes
    if (error.value) {
      error.value = null
    }
  }

  function setIsApplying(value: boolean): void {
    isApplying.value = value
  }

  function setError(errorMessage: string | null): void {
    error.value = errorMessage
  }

  function startEditing(): void {
    isEditing.value = true
    error.value = null
  }

  function cancelEditing(): void {
    isEditing.value = false
    error.value = null
  }

  function resetToState(gameState: any, ctx: any): void {
    const fullState = {
      G: gameState,
      ctx: ctx
    }
    editableJson.value = JSON.stringify(fullState, null, 2)
    error.value = null
  }

  return {
    isEditing,
    editableJson,
    isApplying,
    error,
    setIsEditing,
    setEditableJson,
    setIsApplying,
    setError,
    startEditing,
    cancelEditing,
    resetToState
  }
}

